import React from 'react';
import type { Node } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { IconButton, Title } from '..';
import { useComponentTheme } from '../useComponentTheme';

type Props = {
  children: Node,
  title: ?string,
  className: ?string,
  collapsed?: boolean,
  hasShadow?: boolean,
  hasPadding?: boolean,
  hasMinHeight?: boolean,
  isKiosk?: boolean,
  titleMode: ?string,
  onChangeState?: () => void,
  prefix?: Node,
  mode?: string,
  footer?: Node,
};

export const Accordion = ({
  title,
  children,
  className,
  isKiosk,
  collapsed,
  onChangeState,
  titleMode,
  hasShadow = true,
  hasPadding = true,
  hasMinHeight = true,
  prefix = null,
  mode,
  footer,
}: Props): Node => {
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.Accordion, mode);

  return (
    <AccordionWrapper
      hasMinHeight={hasMinHeight}
      hasShadow={hasShadow}
      hasPadding={hasPadding}
      className={className}
      data-test="accordion-wrapper"
      componentTheme={componentTheme}
    >
      <AccordionHeader data-test="accordion-header" onClick={onChangeState}>
        {prefix}
        <Title margin="none" level={2} data-test="accordion-title" mode={titleMode}>
          {title}
        </Title>
        <ChevronIcon
          data-test="accordion-chevron-icon"
          mode="rounded"
          icon={collapsed ? 'ChevronDown' : 'ChevronUp'}
          fill={theme.color.black}
          size={isKiosk ? 'large' : 'small'}
        />
      </AccordionHeader>
      <AccordionContent isCollapsed={collapsed}>{children}</AccordionContent>
      {footer}
    </AccordionWrapper>
  );
};

const AccordionWrapper = styled('div')`
  background-color: ${({ theme }) => theme.color.white};
  padding: ${({ hasPadding }) => hasPadding && '16px 24px'};
  min-height: ${({ hasMinHeight }) => hasMinHeight && '68px'};
  ${({ componentTheme }) => componentTheme.backgroundColor && `background-color: ${componentTheme.backgroundColor}`};

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.large};
    box-shadow: ${({ theme, hasShadow }) => hasShadow && theme.boxShadow.small};
    padding: ${({ hasPadding }) => hasPadding && '24px 32px'};
    min-height: ${({ hasMinHeight }) => hasMinHeight && '88px'};
  }
`;

const AccordionHeader = styled('header')`
  position: relative;
  display: flex;
  cursor: pointer;
  user-select: none;
  align-items: center;
`;

const AccordionContent = styled('div')`
  margin-top: 24px;
  margin-bottom: 8px;
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'block')};
`;

const ChevronIcon = styled(IconButton)`
  margin-left: auto;
`;

Accordion.defaultProps = {
  className: null,
};
